import React from 'react';
import {
  ActivityIndicator, StyleSheet, Text, View,
} from 'react-native';
import { shallowEqual, useSelector } from 'react-redux';
import ViewShot from 'react-native-view-shot';
import Colors from '../../../constants/Colors';
import CircleChart from '../../../components/CircleChart';
import Style from '../../../constants/Style';
import getPriorityColor from '../../../components/PriorityColorScheme';
import WidgetHeader from './WidgetHeader';
import isMobile from '../../../components/IsMobile';
import { SolveAnalyticsWidgetProps } from '../Utils';
import { Building } from '../../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../../LocationManagement/components/BuildingSettingsModal/data';

export default function PriorityStats({
  range, compareRange, viewShotRef, loading, data, comparisonData,
}: SolveAnalyticsWidgetProps) {
  const compareToggled: boolean = !!compareRange.start && !!compareRange.end;
  const slaNames: string[] = useSelector((state: { selectedBuilding: Building }) => state
    .selectedBuilding.SlaHours?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);

  const LegendItem = ({ index }: { index: number }) => <View style={styles.priorityDesc}>
    <View style={Style.row}>
      <View style={[styles.square, { backgroundColor: getPriorityColor(index) }]} />
      <Text
        style={[styles.label, { marginEnd: 12 }]}
        numberOfLines={1}
      >
        {`P${index + 1} - ${slaNames[index]}`}
      </Text>
    </View>
    <View style={[Style.row, { flex: 1, justifyContent: 'flex-end' }]}>
      <Text style={styles.percentageText}>{Math.round(values[index])}{'%'}</Text>
      {compareToggled && <Text style={[styles.percentageText, styles.comparisonText]}>
        {Math.round(compareValues[index])}{'%'}
      </Text>}
    </View>
  </View>;

  const urgent: number = data?.urgent ?? 0;
  const high: number = data?.high ?? 0;
  const normal: number = data?.moderate ?? 0;
  const low: number = data?.low ?? 0;
  const other: number = data?.other ?? 0;
  const total: number = data?.total ?? 0;
  const compareUrgent: number = comparisonData?.urgent ?? 0;
  const compareHigh: number = comparisonData?.high ?? 0;
  const compareNormal: number = comparisonData?.moderate ?? 0;
  const compareLow: number = comparisonData?.low ?? 0;
  const compareOther: number = comparisonData?.other ?? 0;
  const compareTotal: number = comparisonData?.total ?? 0;
  const values: [number, number, number, number, number] = [urgent, high, normal, low, other];
  const compareValues: [number, number, number, number, number] = [
    compareUrgent, compareHigh, compareNormal, compareLow, compareOther];

  const radius: number = isMobile ? 67 : 38;
  let stroke: number = compareToggled ? 8 : 12;
  if (isMobile) stroke = compareToggled ? 12 : 24;

  return <View
    style={[Style.commonCardStyle, styles.container]}
    testID='sa-tickets-solved-card'
  >
    <WidgetHeader
      title='Priority statistics'
      {...{ range, compareRange }}
      style={{ flex: 1 }}
      tooltipText='Breakdown of tickets raised in the given period by priority'
    />
    <ViewShot
      style={styles.content}
      ref={viewShotRef}
    >
      <View style={styles.chart}>
        <View>
          <View style={Style.row}>
            <Text style={styles.priorityHours}>{total}</Text>
            {compareToggled && <Text style={[styles.priorityHours, styles.comparisonText]}>
              {compareTotal}
            </Text>}
          </View>
          <Text
            style={[styles.label, { textAlign: 'center' }]}
            numberOfLines={1}
          >
            {'Tickets'}
          </Text>
        </View>
        <CircleChart
          {...{
            other,
            low,
            normal,
            high,
            urgent,
            compareOther,
            compareLow,
            compareNormal,
            compareHigh,
            compareUrgent,
            radius,
            stroke,
            compareToggled,
          }}
        />
      </View>
      <View style={[styles.legend]}>
        <View style={styles.legendSection}>
          <LegendItem index={0} />
          <LegendItem index={1} />
          <LegendItem index={2} />
        </View>
        <View style={styles.legendSection}>
          <LegendItem index={3} />
          <LegendItem index={4} />
        </View>
      </View>
      {loading ? <ActivityIndicator
        style={Style.activityIndicator}
        animating
        size='large'
      /> : null}
    </ViewShot>
  </View>;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
  },
  content: {
    ...Style.row,
    flexDirection: isMobile ? 'column' : 'row',
    alignSelf: isMobile ? 'stretch' : undefined,
    flex: 3,
  },
  chart: {
    ...Style.row,
    justifyContent: 'center',
    alignSelf: isMobile ? 'stretch' : undefined,
    flex: 1,
  },
  legend: {
    ...Style.row,
    flexDirection: isMobile ? 'column' : 'row',
    alignSelf: isMobile ? 'stretch' : undefined,
    alignItems: isMobile ? undefined : 'flex-start',
    flex: 1,
  },
  legendSection: {
    flex: 1,
  },
  label: {
    color: Colors.light.hereworksBlack600,
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
    lineHeight: 22,
  },
  priorityDesc: {
    ...Style.row,
    marginVertical: 2,
    marginStart: isMobile ? undefined : 32,
  },
  priorityHours: {
    fontSize: isMobile ? 48 : 30,
    lineHeight: isMobile ? undefined : 30,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 4,
  },
  comparisonText: {
    color: Colors.light.hereworksYellow600,
  },
  percentageText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack600,
    marginStart: 2,
    width: isMobile ? 28 : undefined,
    textAlign: 'right',
  },
  square: {
    width: 16,
    height: 16,
    borderRadius: 2,
    marginRight: 8,
  },
});
